import React, { useEffect, useState } from 'react';
import ActionModal from 'components/common/modals/ActionModal';
import { useRootStore } from 'store/rootStore';
import { FileUpload, Select } from '@insurancemenu/imenu-components-lib';
import { AcceptedFiles, MimeTypesMap } from '@insurancemenu/imenu-components-lib/dist/types/FileUpload';
import { ModalType } from 'global/utils/ModalsHelper';
import { observer } from 'mobx-react-lite';
import {
  REJECTED_FILE_MESSAGES,
  UPLOAD_FILE_TYPES,
} from '@insurancemenu/imenu-components-lib/dist/components/FileUpload';
import { FileExchangeService } from 'types/exchangeService';
import { createStyles } from './styles';

export interface UploadValidation {
  fileMimeTypes: MimeTypesMap[];
  messageForRejectedFiles: string;
}

export interface UploadFileModalProps {
  show: boolean;
  sponsorId: string;
  uploadFileFormat: Exclude<FileExchangeService, FileExchangeService.CSV>;
  allowMultipleFiles?: boolean;
  onUpdate: (sponsor: string) => void;
}

const UploadFileModal = observer(
  ({ show, sponsorId, uploadFileFormat, allowMultipleFiles = false, onUpdate }: UploadFileModalProps): JSX.Element => {
    const classes = createStyles();
    const {
      filesTrackingController: {
        isFileUploadReset,
        loading,
        getSponsorOptions,
        setIsFileUploadReset,
        uploadSponsorFiles,
        closeUploadModal,
      },
    } = useRootStore();

    const [sponsor, setSponsor] = useState(sponsorId);
    const [files, setFiles] = useState<AcceptedFiles>({});

    useEffect(() => {
      setSponsor(sponsorId);
    }, [sponsorId]);

    const UploadValidations: Record<
      Exclude<FileExchangeService, FileExchangeService.CSV>,
      UploadValidation
    > = Object.freeze({
      PDF: {
        fileMimeTypes: [UPLOAD_FILE_TYPES.pdf],
        messageForRejectedFiles: REJECTED_FILE_MESSAGES.pdf,
      },
      EDI: {
        fileMimeTypes: [],
        messageForRejectedFiles: '',
      },
    });

    const onModalClose = (): void => {
      setFiles({});
      setIsFileUploadReset(true);
      closeUploadModal();
      setSponsor(sponsorId);
    };

    const onFileUpload = async (): Promise<void> => {
      // onUpdate -> Update sponsor id on the dashboard and get the updated list of files
      await uploadSponsorFiles(files, sponsor, uploadFileFormat, onUpdate);
      onModalClose();
    };

    const modalContent = (
      <div>
        <div className={classes.additionalInfoHeader}>
          <Select
            label="Sponsor Id"
            name="sponsorId"
            options={getSponsorOptions()}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
              setSponsor(e.target.value);
            }}
            value={sponsor}
            width="23rem"
            validationPassed={!!sponsor}
            validationMessage="Please select sponsor"
            className={classes.sponsorField}
          />
        </div>
        <div className={classes.fileUpload}>
          <FileUpload
            allowMultipleFiles={allowMultipleFiles}
            fileMimeTypes={UploadValidations[uploadFileFormat]?.fileMimeTypes || []}
            messageForRejectedFiles={UploadValidations[uploadFileFormat]?.messageForRejectedFiles || ''}
            shouldBeReset={isFileUploadReset}
            onChange={(acceptedFile): void => {
              setFiles(acceptedFile);
            }}
          />
        </div>
      </div>
    );

    return (
      <div>
        <ActionModal
          show={show}
          title="Upload File"
          subtitle={
            <p className={classes.subtitle}>
              You can upload one or multiple files,
              <br /> provided they are in the correct format.
            </p>
          }
          modalType={ModalType.NO_ICON}
          closeButtonText="Cancel"
          onClose={onModalClose}
          actionButtonText="Upload File"
          onAction={onFileUpload}
          loading={loading}
          modalContent={modalContent}
          isValid={Object.keys(files).length !== 0}
        />
      </div>
    );
  },
);

export default UploadFileModal;
